import { Alert, Checkbox } from 'antd'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

type Props = {
  formIndex?: string
  questionDescription?: string
  checkBoxes?: {
    param: string
    checked: boolean
  }[]
}

export const HtmlAgeDetailFunction = ({
  formIndex,
  questionDescription = 'add initial field',
  checkBoxes,
}: Props) => {
  const [rules, setRules] = useState(
    checkBoxes || [
      {
        param: 'only_number',
        checked: true,
      },
      {
        param: 'include_months',
        checked: false,
      },
    ]
  )

  const RULE_LIST = [
    {
      id: uuid(),
      name: 'Only number',
      param: 'only_number',
    },
    {
      id: uuid(),
      name: 'Include months',
      param: 'include_months',
    },
  ]

  let displayedValue = '5 years'

  const onlyNumber = rules.some(
    (rule) => rule.param === 'only_number' && rule.checked
  )
  const includeMonths = rules.some(
    (r) => r.param === 'include_months' && r.checked
  )

  if (onlyNumber && includeMonths) {
    displayedValue = '5, 1'
  }

  if (onlyNumber && !includeMonths) {
    displayedValue = '5'
  }

  if (!onlyNumber && includeMonths) displayedValue = '5 years, 1 month'

  return (
    <>
      <span data-function="agedetail" data-type="function">
        AGEDETAIL
      </span>
      <span data-type="parentheses">(</span>
      <span
        data-description="Click to add a date field"
        data-form-index={formIndex}
        data-order="0"
        data-type="addDateField"
        data-value={formIndex}
      >
        {questionDescription}
      </span>
      <i data-type="comma" />
      <i data-type="generic" data-value={`only_number=${onlyNumber}`} />
      <i data-type="comma" />
      <i data-type="generic" data-value={`include_months=${includeMonths}`} />
      <span data-type="parentheses">)</span>
      <div
        className="ml-1 mt-3 flex min-h-15 w-11/12 flex-col gap-1 rounded-md border-2 !border-blue-400/20 bg-blue-400/10 px-2.5 py-2"
        data-type="agedetail"
      >
        {RULE_LIST.map((item) => (
          <div
            className="flex w-[fit-content] select-none items-center gap-1 rounded-md px-1.5 py-0.5 transition-all delay-[30ms] hover:bg-blue-400/30"
            key={item.param}
          >
            <Checkbox
              checked={
                rules?.find((rule) => rule?.param === item?.param)?.checked
              }
              data-param={item.param}
              data-type="agedetails-checkbox"
              id={item.id}
              onChange={(event) =>
                setRules((oldRules) =>
                  oldRules.map((rule) =>
                    rule.param === item.param
                      ? { ...rule, checked: event.target.checked }
                      : rule
                  )
                )
              }
            />
            <label
              className="m-0 cursor-pointer text-gray-700"
              htmlFor={item.id}
            >
              {item.name}
            </label>
          </div>
        ))}
        <small className="ml-2">Displayed value: {displayedValue}</small>
      </div>
      {includeMonths || (!onlyNumber && includeMonths) ? (
        <Alert
          showIcon
          className="ml-1 mt-3 w-11/12 [&_.ant-alert-message]:mb-0"
          description='Selecting "Include months" will limit the calculation field to this function only. If there are any existing calculations, they will be cleared and only this one will be kept'
          message={
            <span className="text-sm font-bold">
              Are you sure you want to select include months?
            </span>
          }
          type="warning"
        />
      ) : null}
      {!includeMonths && !onlyNumber ? (
        <Alert
          showIcon
          className="ml-1 mt-3 w-11/12 [&_.ant-alert-message]:mb-0"
          description='Unchecking "only number" will limit the calculation field to this function only. If there are any existing calculations, they will be cleared and only this one will be kept'
          message={
            <span className="text-sm font-bold">
              Are you sure you want to uncheck only number?
            </span>
          }
          type="warning"
        />
      ) : null}
    </>
  )
}
